import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { getPlayerId } from '../utils/playerId';
import { reportPageLoadPerformance } from '../utils/usageEvents';
import ShareModal from './ShareModal.jsx';

const fetch = require("node-fetch");

const STYLE_ORDER = ['affiliative', 'self_enhancing', 'aggressive', 'self_defeating'];
const STYLE_LABELS = {
  affiliative: 'Affiliative',
  self_enhancing: 'Self-enhancing',
  aggressive: 'Aggressive',
  self_defeating: 'Self-defeating',
};

class HumorQuiz extends Component {
  constructor(props) {
    super(props);
    this.state = {
      status: 'loading', // loading | answering | submitting | result | error
      questions: [],
      scale_min: 1,
      scale_max: 7,
      current_index: 0,
      answers: {},
      result: null,
      error_message: '',
      share_modal_open: false,
    };
    this.handle_form_submit = this.handle_form_submit.bind(this);
  }

  componentDidMount() {
    // a no-op if Layout (or another route) already reported this real
    // page load - only matters when a player's very first load lands
    // directly here, e.g. a shared /humor-quiz link
    reportPageLoadPerformance(null);
    this.load();
  }

  async load() {
    try {
      const response = await fetch('/api/humor_quiz/questions');
      const json = await response.json();
      this.setState({
        status: 'answering',
        questions: json.questions,
        scale_min: json.scale_min,
        scale_max: json.scale_max,
      });
    } catch (error) {
      this.setState({ status: 'error' });
    }
  }

  set_answer(question_id, value) {
    this.setState({ answers: Object.assign({}, this.state.answers, { [question_id]: value }) });
  }

  handle_form_submit(event) {
    event.preventDefault();
    this.go_next();
  }

  go_next() {
    const is_last = this.state.current_index === this.state.questions.length - 1;
    if (is_last) {
      this.submit_quiz();
    } else {
      this.setState({ current_index: this.state.current_index + 1 });
    }
  }

  async submit_quiz() {
    this.setState({ status: 'submitting', error_message: '' });
    try {
      const response = await fetch('/api/humor_quiz/submit', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ player: getPlayerId(), answers: this.state.answers }),
      });
      const json = await response.json();
      if (!response.ok) {
        this.setState({ status: 'answering', error_message: json.error || 'Something went wrong' });
        return;
      }
      this.setState({ status: 'result', result: json });
    } catch (error) {
      this.setState({ status: 'answering', error_message: 'Could not reach the server' });
    }
  }

  retake() {
    this.setState({ status: 'answering', current_index: 0, answers: {}, result: null, error_message: '' });
  }

  render() {
    if (this.state.status === 'loading') {
      return <div id="quiz_page"><p>Loading…</p></div>;
    }
    if (this.state.status === 'result') {
      const result = this.state.result;
      return (
        <div id="quiz_page">
          <Link to="/profile" id="quiz_back_link">← Back to your profile</Link>
          <h1 id="quiz_title">Your humor style</h1>
          <p id="quiz_dominant_label">{result.dominant_label}</p>
          <p id="quiz_dominant_blurb">{result.dominant_blurb}</p>
          <div id="quiz_scores">
            {STYLE_ORDER.map(style => (
              <div className="quiz_score_row" key={style}>
                <span className="quiz_score_label">{STYLE_LABELS[style]}</span>
                <div className="quiz_score_bar_track">
                  <div
                    className="quiz_score_bar_fill"
                    style={{ width: (result.scores[style] / 7 * 100) + '%' }}
                  />
                </div>
                <span className="quiz_score_value">{result.scores[style].toFixed(1)}</span>
              </div>
            ))}
          </div>
          <div id="quiz_result_actions">
            <button id="quiz_share" type="button" onClick={() => this.setState({ share_modal_open: true })}>
              Share 📤
            </button>
            <button id="quiz_retake" type="button" onClick={() => this.retake()}>Retake the quiz</button>
          </div>
          {this.state.share_modal_open && (
            <ShareModal
              apiUrl="/api/humor_quiz/share"
              payload={{}}
              title="Share your humor style"
              recipientLabel="Invite someone to find out theirs:"
              onClose={() => this.setState({ share_modal_open: false })}
            />
          )}
        </div>
      );
    }

    const idx = this.state.current_index;
    const question = this.state.questions[idx];
    const total = this.state.questions.length;
    const progress_pct = (idx / total) * 100;
    const is_last = idx === total - 1;
    const current_value = this.state.answers[question.id];
    const submitting = this.state.status === 'submitting';

    return (
      <div id="quiz_page">
        <Link to="/profile" id="quiz_back_link">← Back to your profile</Link>
        <h1 id="quiz_title">What's your humor style?</h1>
        <form onSubmit={this.handle_form_submit}>
          <p id="quiz_question_text">{question.text}</p>
          <div className="quiz_scale">
            {Array.from({ length: this.state.scale_max - this.state.scale_min + 1 }, (_, i) => this.state.scale_min + i).map(value => (
              <label className="quiz_scale_option" key={value}>
                <input
                  type="radio"
                  name={'question_' + question.id}
                  value={value}
                  checked={current_value === value}
                  onChange={() => this.set_answer(question.id, value)}
                />
                <span>{value}</span>
              </label>
            ))}
          </div>
          <p id="quiz_scale_hint">
            {this.state.scale_min} = totally disagree &nbsp;·&nbsp; {this.state.scale_max} = totally agree
          </p>
          {this.state.error_message && (
            <p id="quiz_error" role="alert">{this.state.error_message}</p>
          )}
          <button id="quiz_next" type="submit" disabled={current_value === undefined || submitting}>
            {submitting ? 'Scoring…' : (is_last ? 'See my results' : 'Next →')}
          </button>
          <div id="quiz_progress_track">
            <div id="quiz_progress_fill" style={{ width: progress_pct + '%' }} />
          </div>
          <p id="quiz_progress_label">Question {idx + 1} of {total}</p>
        </form>
      </div>
    );
  }
}

export default HumorQuiz;
